/**
 * Status and comments on a request routed to DSAI.
 *
 * Open to everyone who can see the DSAI page (owner, 2026-10-05): both sides
 * work the same list, and this is where they talk about a request.
 */
import { useState } from 'react'
import {
  Button,
  Caption1,
  Select,
  Text,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  useAddDsaiComment,
  useDsaiComments,
  useDsaiStatuses,
  useSetDsaiStatus,
} from '../hooks/useDigitalCouncil'
import { apiErrorMessage } from '../lib/apiError'
import { useToast } from './ToastProvider'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  statusRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  list: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  comment: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    background: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    whiteSpace: 'pre-wrap',
  },
  meta: { color: tokens.colorNeutralForeground3 },
  composer: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  post: { alignSelf: 'flex-end' },
})

function fmt(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export function DsaiDiscussion({ demandId, status }: { demandId: number; status: string }) {
  const styles = useStyles()
  const { data: statuses = [] } = useDsaiStatuses()
  const setStatus = useSetDsaiStatus(demandId)
  const { data: comments = [] } = useDsaiComments(demandId)
  const add = useAddDsaiComment(demandId)
  const [draft, setDraft] = useState('')
  const { showToast } = useToast()
  const fail = (err: unknown) => showToast({ title: apiErrorMessage(err), intent: 'error' })

  const post = () => {
    const body = draft.trim()
    if (!body) return
    add.mutate(body, { onSuccess: () => setDraft(''), onError: fail })
  }

  return (
    <div className={styles.root}>
      <div className={styles.statusRow}>
        <Text weight="semibold">Status</Text>
        <Select
          aria-label="Status"
          value={status}
          disabled={setStatus.isPending}
          onChange={(_, d) => setStatus.mutate(d.value, { onError: fail })}
          data-testid="dsai-status"
        >
          {statuses.map((s) => (
            <option key={s.code} value={s.code}>
              {s.label}
            </option>
          ))}
        </Select>
      </div>

      <div className={styles.list} data-testid="dsai-comments">
        {comments.length === 0 && <Caption1 className={styles.meta}>No comments yet.</Caption1>}
        {comments.map((c) => (
          <div key={c.id} className={styles.comment}>
            <Caption1 className={styles.meta}>
              {c.author_name ?? 'Someone'} · {fmt(c.created_at)}
            </Caption1>
            <div>{c.body}</div>
          </div>
        ))}
      </div>

      <div className={styles.composer}>
        <Textarea
          aria-label="Comment"
          placeholder="Add a comment"
          value={draft}
          onChange={(_, d) => setDraft(d.value)}
          resize="vertical"
          data-testid="dsai-comment-input"
        />
        <Button
          className={styles.post}
          appearance="primary"
          disabled={!draft.trim() || add.isPending}
          onClick={post}
          data-testid="dsai-comment-post"
        >
          Post
        </Button>
      </div>
    </div>
  )
}
